// Settings → AI engine → Usage: what your AI requests have cost, this month
// and over the last 30 days, by model and request by request, with an
// optional monthly budget for your own key.
//
// Own-key costs are the app's figures (OpenRouter's real ones, otherwise
// tokens × published rates), recorded by apis/llm/usageLog.js. A$ amounts are
// converted at the day's ECB rate when recorded.

import React, { useCallback, useContext, useEffect, useState } from "react";
import { ArrowLeftRight, CalendarDays, FileSpreadsheet, Info, Loader2, PenLine, Sparkles, Wrench } from "lucide-react";
import DetailSheet from "../session/generate/DetailSheet";
import { OptionList } from "../profile/ProfileParts";
import api from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { cn } from "../../lib/utils";
import { SYMBOLS, describeRate, displayCurrency, formatAmount } from "../../utils/money";
import { dayName, firstName, formatTime, groupByDay, shortModel, todayKey } from "../../utils/creditShares";
import { ActivityRow, DayHeading, Meter, SheetGroup } from "./creditParts";
import { describeChange, formatDuration, niceMax } from "../../utils/usageReport";

// Categorical slots 1 and 2 of the dataviz palette, validated together on the
// app's dark surface (#0F1114): blue for your own key, orange for a mate's.
const OWN = "#3987e5";
const SHARED = "#d95926";

const OPERATIONS = {
  session: { label: "Session", icon: Sparkles },
  describe: { label: "Described workout", icon: PenLine },
  review: { label: "Programme review", icon: CalendarDays },
  import: { label: "Programme import", icon: FileSpreadsheet },
  exercise: { label: "Exercise details", icon: Info },
  substitute: { label: "Exercise swaps", icon: ArrowLeftRight },
  repair: { label: "Session repair", icon: Wrench },
};

const BUDGET_STEPS = { AUD: [2, 5, 10, 20], USD: [1, 2, 5, 10] };

const money = (amount, currency, precise = false) => formatAmount(Number(amount) || 0, currency, { precise });

const axisLabel = (value, currency) => `${SYMBOLS[currency]}${Number(value.toFixed(3))}`;

const UsageChart = ({ daily, currency, hasShared }) => {
  const [selected, setSelected] = useState(daily.length - 1);
  const top = niceMax(Math.max(...daily.map((d) => d.own + d.share)));
  const pick = daily[selected] || daily[daily.length - 1];
  const total = pick ? pick.own + pick.share : 0;
  const today = todayKey();
  const longDay = (date) =>
    date === today
      ? "Today"
      : new Date(`${date}T12:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
  const height = 120;
  const px = (v) => (v / top) * height;

  return (
    <div className="rounded-xl border border-border bg-background px-3 pb-2 pt-3">
      <div aria-live="polite" className="flex min-h-[38px] items-start justify-between gap-2">
        <span>
          <span className="block text-xs text-muted-foreground">{pick ? longDay(pick.date) : ""}</span>
          <span className="block text-[15px] font-semibold tabular-nums">
            {total > 0 ? money(total, currency, true) : "Nothing"}
          </span>
        </span>
        <span className="whitespace-pre-line text-right text-xs text-muted-foreground">
          {pick?.requests
            ? `${pick.sessions ? `${pick.sessions} ${pick.sessions === 1 ? "session" : "sessions"}` : `${pick.requests} requests`}${
                pick.share > 0 ? `\n${money(pick.share, currency, true)} on mates' credits` : ""
              }`
            : "No AI used"}
        </span>
      </div>
      <div className="relative mt-2" style={{ height }}>
        {[0, 0.5].map((f) => (
          <div key={f} className="absolute left-0 right-10 border-t border-border" style={{ top: f * height }} />
        ))}
        <div className="absolute bottom-0 left-0 right-10 border-t border-muted" />
        <span className="absolute -top-[7px] right-0 font-mono text-[10px] text-muted-foreground">{axisLabel(top, currency)}</span>
        <span className="absolute right-0 font-mono text-[10px] text-muted-foreground" style={{ top: height / 2 - 7 }}>
          {axisLabel(top / 2, currency)}
        </span>
        <span className="absolute -bottom-[7px] right-0 font-mono text-[10px] text-muted-foreground">0</span>
        <div
          className="absolute inset-y-0 left-0 right-10 grid"
          style={{ gridTemplateColumns: `repeat(${daily.length}, minmax(0, 1fr))` }}
        >
          {daily.map((d, i) => {
            const ownH = px(d.own);
            const shareH = px(d.share);
            return (
              <button
                key={d.date}
                type="button"
                aria-pressed={i === selected}
                aria-label={`${longDay(d.date)}: ${money(d.own + d.share, currency, true)}`}
                onClick={() => setSelected(i)}
                className={cn("flex h-full flex-col items-center justify-end rounded", i === selected && "bg-foreground/[0.08]")}
              >
                {shareH > 0 && (
                  <span
                    className={cn("block w-1.5 rounded-t-[3px]", ownH > 0 && "mb-0.5")}
                    style={{ height: Math.max(2, shareH), background: SHARED }}
                  />
                )}
                {ownH > 0 && (
                  <span
                    className={cn("block w-1.5", shareH > 0 ? "" : "rounded-t-[3px]")}
                    style={{ height: Math.max(2, ownH), background: OWN }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mr-10 flex justify-between pt-1.5 font-mono text-[10px] text-muted-foreground">
        <span>{daily[0] && dayName(daily[0].date)}</span>
        <span>{daily[Math.floor(daily.length / 2)] && dayName(daily[Math.floor(daily.length / 2)].date)}</span>
        <span>Today</span>
      </div>
      <div className="sr-only">
        <table>
          <caption>AI spend by day, last 30 days</caption>
          <thead>
            <tr>
              <th>Day</th>
              <th>Your key</th>
              {hasShared && <th>Mates&apos; credits</th>}
            </tr>
          </thead>
          <tbody>
            {daily.map((d) => (
              <tr key={d.date}>
                <td>{d.date}</td>
                <td>{money(d.own, currency, true)}</td>
                {hasShared && <td>{money(d.share, currency, true)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Legend = () => (
  <span className="flex gap-3 text-xs text-muted-foreground">
    <span className="flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-sm" style={{ background: OWN }} />
      Your key
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-sm" style={{ background: SHARED }} />
      Mates&apos; credits
    </span>
  </span>
);

/** This month's total for the Settings row; reload after the sheet closes. */
export const useUsageTotal = (enabled, currency) => {
  const [total, setTotal] = useState(null);
  const load = useCallback(() => {
    if (!enabled) return;
    api
      .getUsageReport(currency)
      .then((r) => setTotal(r?.thisMonth ? money(r.thisMonth.cost, r.currency) : null))
      .catch(() => setTotal(null));
  }, [enabled, currency]);
  useEffect(() => {
    load();
  }, [load]);
  return { total, reload: load };
};

const RECENT_SHOWN = 6;

const UsageSheet = ({ open, onClose }) => {
  const { money: moneySettings } = useContext(WorkoutContext);
  const { showToast } = useToast();
  const currency = displayCurrency(moneySettings);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState("main");
  const [recentAll, setRecentAll] = useState(false);

  const load = useCallback(async () => {
    try {
      setReport(await api.getUsageReport(currency));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [currency]);

  useEffect(() => {
    if (!open) return;
    setView("main");
    setRecentAll(false);
    load();
  }, [open, load]);

  const setBudget = async (value) => {
    setView("main");
    try {
      await api.setUsageBudget(value === "none" ? { amount: null } : { amount: Number(value), currency: report.currency });
      await load();
    } catch (e) {
      showToast({ title: "Couldn't save your budget", description: e.message });
    }
  };

  const c = report?.currency || currency;
  const month = report?.thisMonth;
  const monthName = report?.month
    ? new Date(`${report.month}-15T12:00:00`).toLocaleDateString("en-AU", { month: "long" })
    : "This month";
  const hasShared = Boolean(report?.daily?.some((d) => d.share > 0) || month?.shareCost > 0);
  const budget = report?.budget;
  const budgetMeter = budget
    ? (() => {
        const pct = Math.min(100, (budget.spent / budget.amount) * 100);
        return { pct, tone: pct >= 100 ? "danger" : pct >= 80 ? "warning" : "normal" };
      })()
    : null;
  const recent = report?.recent || [];
  const recentShown = recentAll ? recent : recent.slice(0, RECENT_SHOWN);

  const mainView = !report ? (
    error ? (
      <p className="text-sm text-destructive">{error}</p>
    ) : (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your usage…
      </p>
    )
  ) : (
    <>
      <div className="grid gap-3 rounded-2xl border border-border bg-background p-4">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{monthName} so far</span>
          <span className="text-xs text-muted-foreground">{describeChange(month, report.lastMonth, report.month)}</span>
        </div>
        <div className="text-5xl font-semibold leading-none tracking-tight">{money(month.cost, c)}</div>
        <div className="text-[13px] text-muted-foreground">
          {month.sessions
            ? `${month.sessions} ${month.sessions === 1 ? "session" : "sessions"} written${
                month.perSession != null ? ` · ${money(month.perSession, c, true)} each` : ""
              }`
            : month.requests
              ? `${month.requests} AI ${month.requests === 1 ? "request" : "requests"}`
              : "Nothing yet this month"}
        </div>
        {hasShared && (
          <div className="grid gap-1.5 pt-1">
            {[
              ["Your key", OWN, month.ownCost],
              ["Mates' credits", SHARED, month.shareCost],
            ].map(([label, colour, amount]) => (
              <div key={label} className="grid grid-cols-[10px_minmax(0,1fr)_auto] items-center gap-2 text-[13px]">
                <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: colour }} />
                <span>{label}</span>
                <span className="tabular-nums">{money(amount, c)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => setView("budget")}
        className="grid w-full gap-2.5 rounded-xl border border-border bg-background p-3 text-left transition-colors hover:bg-muted/30"
      >
        <span className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2.5">
          <span className="text-sm font-medium">Monthly budget</span>
          <span className="text-[13px] tabular-nums">
            {budget ? `${money(budget.spent, budget.currency)} of ${money(budget.amount, budget.currency)}` : "Not set"}
          </span>
        </span>
        {budget && (
          <>
            <Meter meter={budgetMeter} thick label="Monthly budget used" />
            <span className="text-xs text-muted-foreground">
              {budgetMeter.pct >= 100
                ? "Budget reached. Sessions keep working."
                : budgetMeter.pct >= 80
                  ? "Over 80% of your budget."
                  : "Your key only. You're told at 80% and 100%."}
            </span>
          </>
        )}
      </button>

      <section className="grid gap-2">
        <div className="flex items-baseline justify-between gap-2 px-0.5">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Last 30 days</h3>
          {hasShared && <Legend />}
        </div>
        <UsageChart daily={report.daily} currency={c} hasShared={hasShared} />
      </section>

      {report.byModel.length > 0 && (
        <SheetGroup title="By model" aside="Last 90 days">
          {report.byModel.map((m) => (
            <ActivityRow
              key={`${m.provider}/${m.model}`}
              title={shortModel(m.model) || m.provider}
              detail={[
                m.sessions ? `${m.sessions} ${m.sessions === 1 ? "session" : "sessions"}` : `${m.requests} requests`,
                m.medianMs ? `usually ${formatDuration(m.medianMs)}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              amount={m.perSession != null ? money(m.perSession, c, true) : money(m.cost, c, true)}
              note={m.perSession != null ? "a session" : "in total"}
            />
          ))}
        </SheetGroup>
      )}

      <SheetGroup title="Recent">
        {recent.length === 0 ? (
          <p className="px-3 py-3 text-sm text-muted-foreground">
            Nothing yet. Every session, described workout and programme review the AI writes shows here.
          </p>
        ) : (
          <>
            {groupByDay(recentShown, (r) => todayKey(new Date(r.at))).map((group) => (
              <React.Fragment key={group.key}>
                <DayHeading>{group.label}</DayHeading>
                {group.items.map((r, i) => {
                  const op = OPERATIONS[r.operation] || OPERATIONS.session;
                  return (
                    <ActivityRow
                      key={`${r.at}-${i}`}
                      icon={op.icon}
                      title={r.ok ? op.label : `${op.label} (failed)`}
                      badge={
                        r.paidBy === "share" ? (
                          <span className="flex items-center gap-1 whitespace-nowrap text-[11px] text-muted-foreground">
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: SHARED }} />
                            {r.shareOwner ? `${firstName(r.shareOwner)}'s credits` : "Shared credits"}
                          </span>
                        ) : null
                      }
                      detail={[shortModel(r.model), formatTime(r.at), formatDuration(r.durationMs)].filter(Boolean).join(" · ")}
                      amount={r.kind === "free" ? "Free" : r.cost == null ? "–" : money(r.cost, c, true)}
                      note={r.kind === "estimated" ? "est." : r.kind === "actual" ? "exact" : undefined}
                    />
                  );
                })}
              </React.Fragment>
            ))}
            {recent.length > RECENT_SHOWN && (
              <button
                type="button"
                onClick={() => setRecentAll((a) => !a)}
                className="w-full px-3 py-2.5 text-left text-sm font-semibold text-primary transition-colors hover:bg-muted/30"
              >
                {recentAll ? "Show fewer" : `Show all ${recent.length}`}
              </button>
            )}
          </>
        )}
      </SheetGroup>

      <p className="text-xs leading-snug text-muted-foreground">
        &ldquo;est.&rdquo; costs are worked out from the provider&rsquo;s published prices; OpenRouter reports exact
        ones.{describeRate(moneySettings) ? ` A$ uses ${describeRate(moneySettings)}.` : ""}
        {report.since
          ? ` Tracking started ${new Date(report.since).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}.`
          : ""}
      </p>
    </>
  );

  const budgetCurrency = report?.currency || currency;
  const own = month?.ownCost || 0;
  const budgetView = report && (
    <>
      <p className="text-[13px] leading-snug text-muted-foreground">
        A monthly limit for your own key. You&rsquo;ll get a notification at 80% and 100%. Sessions keep working; it
        only warns.
      </p>
      <OptionList
        options={[
          ...BUDGET_STEPS[budgetCurrency].map((n) => ({
            value: String(n),
            label: formatAmount(n, budgetCurrency).replace(".00", ""),
            description: `You've used ${Math.round((own / n) * 100)}% this month`,
            aside: budget && budget.amount === n ? "Current" : undefined,
          })),
          { value: "none", label: "No budget", description: "No alerts about spending", aside: budget ? undefined : "Current" },
        ]}
        value={budget ? String(budget.amount) : "none"}
        onChange={setBudget}
      />
      <p className="text-xs leading-snug text-muted-foreground">
        Shared credits don&rsquo;t count: they&rsquo;re your mate&rsquo;s money, with their own limit.
      </p>
    </>
  );

  return (
    <DetailSheet
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={view === "budget" ? "Monthly budget" : "Usage"}
      onBack={view === "budget" ? () => setView("main") : undefined}
    >
      {view === "budget" ? budgetView : mainView}
    </DetailSheet>
  );
};

export default UsageSheet;
